import { mapClientPermissionToResellerRole } from 'src/constants/constants';

const permissionKeyFromUnknown = (raw: unknown): string => {
  if (typeof raw === 'string') return raw.trim().toLowerCase();
  if (raw && typeof raw === 'object') {
    const rec = raw as Record<string, unknown>;
    const identifier = rec.identifier ?? rec.key ?? rec.name ?? rec.permissionIdentifier;
    if (typeof identifier === 'string') return identifier.trim().toLowerCase();

    const nested = rec.permission;
    if (nested && typeof nested === 'object') {
      const nestedRec = nested as Record<string, unknown>;
      const nestedId = nestedRec.identifier ?? nestedRec.key ?? nestedRec.name;
      if (typeof nestedId === 'string') return nestedId.trim().toLowerCase();
    }
  }
  return '';
};

/** Lowercased JWT/role permission identifiers for the same helpers as org entitlements. */
export const toPermissionKeySet = (permissions: unknown): ReadonlySet<string> => {
  if (!Array.isArray(permissions)) return new Set();
  const keys = new Set<string>();
  permissions.forEach((raw) => {
    const key = permissionKeyFromUnknown(raw);
    if (key) keys.add(key);
  });
  return keys;
};

/** Reseller JWT role ids use `reseller_*`; subscription/catalog ids use `client_*`. */
export const mapResellerRoleKeyToCatalogKey = (permission: string): string | undefined => {
  const normalized = String(permission ?? '').trim().toLowerCase();
  if (!normalized.startsWith('reseller_')) return undefined;
  return `client_${normalized.slice('reseller_'.length)}`;
};

/** Normalize registry/subscription permission keys (`reseller_edr_*` → `client_edr_*`). */
export const normalizeRegistryPermissionKey = (permission: string): string => {
  const normalized = String(permission ?? '').trim().toLowerCase();
  return mapResellerRoleKeyToCatalogKey(normalized) ?? normalized;
};

/**
 * EDR Clients is JWT-role only (not org subscription entitlements), including for
 * subscribed client orgs. Matches `client_edr_clients-read` and `client_edr_clients_*`.
 */
export const isRoleOnlyEdrPermission = (permission: string): boolean => {
  const id = normalizeRegistryPermissionKey(permission);
  return id === 'client_edr_clients-read' || id.startsWith('client_edr_clients_');
};

/**
 * JWT role keys with catalog aliases so `canAccess` resolves either prefix.
 * Keeps original keys and adds the mirrored `client_*` / `reseller_*` form.
 */
export const expandRolePermissionKeys = (permissions: unknown): ReadonlySet<string> => {
  const keys = toPermissionKeySet(permissions);
  const expanded = new Set(keys);

  keys.forEach((key) => {
    const catalogKey = mapResellerRoleKeyToCatalogKey(key);
    if (catalogKey) expanded.add(catalogKey);

    const resellerKey = mapClientPermissionToResellerRole(key);
    if (resellerKey !== key) expanded.add(resellerKey);
  });

  return expanded;
};

export const hasExplicitPermission = (
  keys: ReadonlySet<string>,
  permission: string,
): boolean => keys.has(String(permission ?? '').trim().toLowerCase());
